import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { searchMedia, searchNews } from "@/utils/media";

type ResultTab = "全部" | "电影" | "电视剧" | "资讯";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<ResultTab>("全部");

  useEffect(() => {
    setInput(query);
    setTab("全部");
  }, [query]);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 茶杯狐影视` : "站内搜索 - 茶杯狐影视",
    query
      ? `茶杯狐影视中与“${query}”相关的影视作品与资讯搜索结果。`
      : "在茶杯狐影视中搜索电影、电视剧与影视资讯。",
  );

  const mediaResults = useMemo(() => searchMedia(query), [query]);
  const newsResults = useMemo(() => searchNews(query), [query]);

  const movieResults = mediaResults.filter((m) => m.type === "movie");
  const tvResults = mediaResults.filter((m) => m.type === "tv");

  const shownMedia =
    tab === "电影" ? movieResults : tab === "电视剧" ? tvResults : mediaResults;
  const showNews = tab === "资讯" || tab === "全部";
  const totalCount = shownMedia.length + (showNews ? newsResults.length : 0);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const clearSearch = () => {
    setInput("");
    setSearchParams({});
  };

  const tabs: { key: ResultTab; label: string; count: number }[] = [
    { key: "全部", label: "全部", count: mediaResults.length + newsResults.length },
    { key: "电影", label: "电影", count: movieResults.length },
    { key: "电视剧", label: "电视剧", count: tvResults.length },
    { key: "资讯", label: "资讯", count: newsResults.length },
  ];

  return (
    <div className="w-full">
      <section className="border-b border-background-200 bg-primary-950">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs text-primary-200">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-50">搜索</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            站内搜索
          </h1>
          <form onSubmit={submit} className="mt-5 flex max-w-2xl gap-2">
            <div className="flex flex-1 items-center rounded-md border border-background-50/20 bg-background-50/10 px-3 backdrop-blur-sm focus-within:border-accent-400">
              <i className="ri-search-line text-background-200" aria-hidden="true" />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="搜索影视名称、主演或资讯关键词"
                className="w-full bg-transparent px-2 py-3 text-sm text-background-50 outline-none placeholder:text-primary-300"
              />
            </div>
            <button
              type="submit"
              className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-accent-950 transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-line" aria-hidden="true" />
              搜索
            </button>
          </form>
        </div>
      </section>

      <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
        {query ? (
          <>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <p className="text-sm text-foreground-600">
                关键词
                <span className="mx-1 font-bold text-primary-700">“{query}”</span>
                共找到
                <span className="mx-1 font-bold text-accent-600">{totalCount}</span>
                条结果
              </p>
              <button
                type="button"
                onClick={clearSearch}
                className="flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600"
              >
                <i className="ri-close-line" aria-hidden="true" />
                清空搜索
              </button>
            </div>

            <div className="mt-5 flex items-center gap-1 overflow-x-auto rounded-full bg-background-100 p-1 no-scrollbar">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === item.key
                      ? "bg-primary-600 font-semibold text-background-50"
                      : "text-foreground-600 hover:text-primary-700"
                  }`}
                >
                  {item.label}
                  <span
                    className={`rounded-full px-1.5 text-[11px] ${
                      tab === item.key
                        ? "bg-background-50/25 text-background-50"
                        : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {item.count}
                  </span>
                </button>
              ))}
            </div>

            {totalCount > 0 ? (
              <div className="mt-7 flex flex-col gap-10">
                {shownMedia.length > 0 ? (
                  <div>
                    <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="flex h-8 w-1.5 rounded-full bg-accent-500" />
                      影视作品
                      <span className="text-sm font-normal text-foreground-400">
                        {shownMedia.length} 部
                      </span>
                    </h2>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                      {shownMedia.map((item, index) => (
                        <MovieCard key={item.id} item={item} index={index} />
                      ))}
                    </div>
                  </div>
                ) : null}

                {showNews && newsResults.length > 0 ? (
                  <div>
                    <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="flex h-8 w-1.5 rounded-full bg-secondary-500" />
                      影视资讯
                      <span className="text-sm font-normal text-foreground-400">
                        {newsResults.length} 条
                      </span>
                    </h2>
                    <div className="flex flex-col gap-4">
                      {newsResults.map((item, index) => (
                        <NewsCard key={item.id} item={item} index={index} showCover />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-4 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                  没有找到与“{query}”相关的内容
                </h3>
                <p className="mt-2 max-w-sm text-sm text-foreground-500">
                  试试更换关键词，或浏览下方的影视分类与资讯频道。
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/category/movie"
                    className="rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
                  >
                    浏览电影
                  </Link>
                  <Link
                    to="/news"
                    className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400"
                  >
                    查看资讯
                  </Link>
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400"
                  >
                    清空搜索词
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-4 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 text-primary-600">
                <i className="ri-search-line text-3xl" aria-hidden="true" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                输入关键词开始搜索
              </h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                可以搜索影视名称、主演、类型，或影视资讯标题与摘要。
              </p>
            </div>

            <div>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="flex h-8 w-1.5 rounded-full bg-accent-500" />
                热门搜索
              </h2>
              <div className="flex flex-wrap gap-2">
                {["星河归途", "雾都谜案", "科幻", "悬疑", "国漫", "资讯"].map(
                  (word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => setSearchParams({ q: word })}
                      className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                    >
                      {word}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}